import { initializeApp } from 'firebase/app';
import { getFirestore, collection, addDoc, Timestamp } from 'firebase/firestore';

// Debug logging for environment variables
console.log('🔧 Firebase Environment Variables Check:');
console.log('REACT_APP_FIREBASE_PROJECT_ID:', process.env.REACT_APP_FIREBASE_PROJECT_ID);
console.log('REACT_APP_FIREBASE_API_KEY:', process.env.REACT_APP_FIREBASE_API_KEY ? 'Set' : 'Missing');
console.log('REACT_APP_FIREBASE_AUTH_DOMAIN:', process.env.REACT_APP_FIREBASE_AUTH_DOMAIN);

// Firebase configuration with fallback values
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY || 'AIzaSyB3Jbp_rzRStkUGRVG1om_noB_l9dECMGM',
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || 'elev8-website-a155a.firebaseapp.com',
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || 'elev8-website-a155a',
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || 'elev8-website-a155a.firebasestorage.app',
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || '962598553444',
  appId: process.env.REACT_APP_FIREBASE_APP_ID || '1:962598553444:web:b32d17e628dadcee751c71'
};

console.log('🔥 Initializing Firebase with config:', {
  projectId: firebaseConfig.projectId,
  authDomain: firebaseConfig.authDomain,
  apiKey: firebaseConfig.apiKey ? 'Set' : 'Missing'
});

// Initialize Firebase
const app = initializeApp(firebaseConfig);
export const db = getFirestore(app);
export { app }; // Export app for use in other services

// Types for form data
export interface ContactFormData {
  name: string;
  email: string;
  phone: string;
  service: string;
  message: string;
  urgency?: string;
  source?: string;
  smsConsent?: boolean;
}

export interface QuoteFormData {
  name: string;
  email: string;
  phone: string;
  address: string;
  services: string[];
  propertyType: string;
  squareFootage?: number;
  windowCount?: number;
  solarPanelCount?: number;
  linearFeet?: number;
  upsellSquareFootage?: number;
  upsellWindowCount?: number;
  upsellSolarPanelCount?: number;
  upsellLinearFeet?: number;
  stories?: string;
  recurringService?: string;
  additionalDetails?: string;
  smsConsent?: boolean;
}

// New types for organized data structure
export interface EmailData {
  email: string;
  source: string;
  timestamp: any;
  status: string;
}

export interface ClientProfileData {
  name: string;
  email: string;
  phone?: string;
  address?: string;
  serviceInquired: string;
  source: string;
  timestamp?: any;
  status: string;
  additionalInfo?: any;
  smsConsent?: boolean;
}

export interface CommercialFormData {
  businessName: string;
  contactName: string;
  phoneNumber: string;
  businessAddress: string;
  smsConsent?: boolean;
}

// SMS consent record stored in the sms-opt-ins / sms-opt-outs collections (A2P 10DLC compliance)
export interface SmsConsentRecord {
  phone: string;
  name?: string;
  email?: string;
  optedIn: boolean;
  consentText: string;
  source: string;
  timestamp: any;
}

// The exact disclosure shown next to the SMS consent checkbox at every collection point.
// Stored with each consent record as proof of the language the user agreed to.
export const SMS_CONSENT_TEXT =
  'By checking this box, you agree to receive informational and appointment-related text messages ' +
  '(e.g., quotes, scheduling confirmations, service updates) from Elev8 Cleaning Solutions at the phone number provided. ' +
  'Consent is not a condition of purchase. Message frequency varies. Message & data rates may apply. ' +
  'Reply STOP to opt out or HELP for help. See our Privacy Policy and Terms of Service.';

// Normalize phone numbers to a consistent format for storage/deduplication
const normalizePhone = (phone: string): string => {
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  return digits ? `+${digits}` : '';
};

/**
 * Record SMS consent, sorted into separate collections:
 *  - Opt-ins  -> 'sms-opt-ins'
 *  - Opt-outs -> 'sms-opt-outs'
 * Called from every form that collects a phone number.
 */
export const saveSmsConsent = async (
  phone: string,
  optedIn: boolean,
  source: string,
  name?: string,
  email?: string
) => {
  const normalizedPhone = normalizePhone(phone);
  if (!normalizedPhone) {
    console.warn('saveSmsConsent skipped: no valid phone number provided');
    return null;
  }

  try {
    const consentRecord: SmsConsentRecord = {
      phone: normalizedPhone,
      ...(name && { name }),
      ...(email && { email }),
      optedIn,
      consentText: SMS_CONSENT_TEXT,
      source,
      timestamp: Timestamp.now()
    };

    const targetCollection = optedIn ? 'sms-opt-ins' : 'sms-opt-outs';
    const docRef = await addDoc(collection(db, targetCollection), consentRecord);
    console.log(`SMS ${optedIn ? 'opt-in' : 'opt-out'} recorded with ID:`, docRef.id);
    return docRef.id;
  } catch (error) {
    console.error('Error saving SMS consent record:', error);
    // Non-fatal: consent is also stored on the main submission document
    return null;
  }
};

// Save email only (for popups, newsletter signups, etc.)
export const saveEmail = async (email: string, source: string = 'popup') => {
  try {
    const emailData: EmailData = {
      email,
      source,
      timestamp: Timestamp.now(),
      status: 'new'
    };
    
    const docRef = await addDoc(collection(db, 'Emails'), emailData);
    console.log('Email saved with ID: ', docRef.id);
    return docRef.id;
  } catch (error) {
    console.error('Error saving email:', error);
    throw error;
  }
};

// Save full client profile
export const saveClientProfile = async (profileData: ClientProfileData) => {
  try {
    const docRef = await addDoc(collection(db, 'client-profiles'), {
      ...profileData,
      timestamp: Timestamp.now()
    });
    console.log('Client profile saved with ID: ', docRef.id);
    return docRef.id;
  } catch (error) {
    console.error('Error saving client profile:', error);
    throw error;
  }
};

// Save contact form data to Firestore (updated to also save client profile)
export const saveContactForm = async (formData: ContactFormData) => {
  console.log('🔥 Starting contact form save...', formData);
  
  try {
    // Check if Firebase is properly initialized
    if (!db) {
      throw new Error('Firebase database not initialized');
    }
    
    console.log('📝 Saving to contacts collection...');
    // Save to original contacts collection for backward compatibility
    const docRef = await addDoc(collection(db, 'contacts'), {
      ...formData,
      smsConsent: formData.smsConsent === true,
      timestamp: Timestamp.now(),
      status: 'new',
      source: formData.source || 'website'
    });
    console.log('✅ Contact saved with ID: ', docRef.id);

    // Record SMS opt-in/opt-out when a phone number was provided (non-blocking)
    if (formData.phone) {
      saveSmsConsent(
        formData.phone,
        formData.smsConsent === true,
        formData.source || 'contact-form',
        formData.name,
        formData.email
      );
    }

    console.log('👤 Creating client profile...');
    // Also save to Client Profiles collection (non-blocking - don't fail the main operation)
    const clientProfile: ClientProfileData = {
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      serviceInquired: formData.service,
      source: formData.source || 'contact-form',
      status: 'new',
      smsConsent: formData.smsConsent === true,
      additionalInfo: {
        message: formData.message,
        // Only include urgency if it's defined
        ...(formData.urgency && { urgency: formData.urgency })
      }
    };
    
    // Save client profile without blocking the main operation
    saveClientProfile(clientProfile).catch(profileError => {
      console.warn('⚠️ Client profile save failed (main contact still saved):', profileError);
    });
    
    console.log('✅ Contact form submission completed successfully');
    
    return docRef.id;
  } catch (error) {
    console.error('❌ Error saving contact:', error);
    console.error('Error details:', {
      name: (error as any)?.name,
      message: (error as any)?.message,
      code: (error as any)?.code,
      stack: (error as any)?.stack
    });
    throw error;
  }
};

// Save quote request data to Firestore (updated to also save client profile)
export const saveQuoteRequest = async (formData: QuoteFormData) => {
  try {
    // Save to original quotes collection for backward compatibility
    const filteredFormData = {
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      address: formData.address,
      services: formData.services,
      propertyType: formData.propertyType,
      // Only include defined values
      ...(formData.squareFootage !== undefined && formData.squareFootage !== null && { squareFootage: formData.squareFootage }),
      ...(formData.windowCount !== undefined && formData.windowCount !== null && { windowCount: formData.windowCount }),
      ...(formData.solarPanelCount !== undefined && formData.solarPanelCount !== null && { solarPanelCount: formData.solarPanelCount }),
      ...(formData.linearFeet !== undefined && formData.linearFeet !== null && { linearFeet: formData.linearFeet }),
      ...(formData.upsellSquareFootage !== undefined && formData.upsellSquareFootage !== null && { upsellSquareFootage: formData.upsellSquareFootage }),
      ...(formData.upsellWindowCount !== undefined && formData.upsellWindowCount !== null && { upsellWindowCount: formData.upsellWindowCount }),
      ...(formData.upsellSolarPanelCount !== undefined && formData.upsellSolarPanelCount !== null && { upsellSolarPanelCount: formData.upsellSolarPanelCount }),
      ...(formData.upsellLinearFeet !== undefined && formData.upsellLinearFeet !== null && { upsellLinearFeet: formData.upsellLinearFeet }),
      ...(formData.stories && { stories: formData.stories }),
      ...(formData.recurringService && { recurringService: formData.recurringService }),
      ...(formData.additionalDetails && { additionalDetails: formData.additionalDetails })
    };
    
    const docRef = await addDoc(collection(db, 'quotes'), {
      ...filteredFormData,
      smsConsent: formData.smsConsent === true,
      timestamp: Timestamp.now(),
      status: 'pending',
      estimatedPrice: null,
      followUpDate: null
    });
    console.log('Quote request saved with ID: ', docRef.id);

    // Record SMS opt-in/opt-out when a phone number was provided (non-blocking)
    if (formData.phone) {
      saveSmsConsent(
        formData.phone,
        formData.smsConsent === true,
        'quote-form',
        formData.name,
        formData.email
      );
    }

    // Also save to Client Profiles collection
    const clientProfile: ClientProfileData = {
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      address: formData.address,
      serviceInquired: formData.services.join(', '),
      source: 'quote-form',
      status: 'pending',
      smsConsent: formData.smsConsent === true,
      additionalInfo: {
        services: formData.services,
        propertyType: formData.propertyType,
        // Only include defined values to avoid Firestore undefined field errors
        ...(formData.squareFootage !== undefined && formData.squareFootage !== null && { squareFootage: formData.squareFootage }),
        ...(formData.windowCount !== undefined && formData.windowCount !== null && { windowCount: formData.windowCount }),
        ...(formData.solarPanelCount !== undefined && formData.solarPanelCount !== null && { solarPanelCount: formData.solarPanelCount }),
        ...(formData.linearFeet !== undefined && formData.linearFeet !== null && { linearFeet: formData.linearFeet }),
        ...(formData.upsellSquareFootage !== undefined && formData.upsellSquareFootage !== null && { upsellSquareFootage: formData.upsellSquareFootage }),
        ...(formData.upsellWindowCount !== undefined && formData.upsellWindowCount !== null && { upsellWindowCount: formData.upsellWindowCount }),
        ...(formData.upsellSolarPanelCount !== undefined && formData.upsellSolarPanelCount !== null && { upsellSolarPanelCount: formData.upsellSolarPanelCount }),
        ...(formData.upsellLinearFeet !== undefined && formData.upsellLinearFeet !== null && { upsellLinearFeet: formData.upsellLinearFeet }),
        ...(formData.stories && { stories: formData.stories }),
        ...(formData.recurringService && { recurringService: formData.recurringService }),
        ...(formData.additionalDetails && { additionalDetails: formData.additionalDetails })
      }
    };
    
    // Save client profile without blocking the main operation
    saveClientProfile(clientProfile).catch(profileError => {
      console.warn('⚠️ Client profile save failed (main quote still saved):', profileError);
    });

    return docRef.id;
  } catch (error) {
    console.error('Error saving quote request:', error);
    throw error;
  }
};

// Save bundle selection data (updated to also save client profile)
export const saveBundleSelection = async (bundleName: string, customerInfo: any) => {
  try {
    // Save to original collection for backward compatibility
    const docRef = await addDoc(collection(db, 'bundle-selections'), {
      bundleName,
      customerInfo,
      timestamp: Timestamp.now(),
      status: 'new'
    });
    console.log('Bundle selection saved with ID: ', docRef.id);

    // Record SMS opt-in/opt-out when a phone number was provided (non-blocking)
    if (customerInfo.phone) {
      saveSmsConsent(
        customerInfo.phone,
        customerInfo.smsConsent === true,
        'bundle-selection',
        customerInfo.name,
        customerInfo.email
      );
    }

    // Also save to Client Profiles collection if customer info is provided
    if (customerInfo.email) {
      const clientProfile: ClientProfileData = {
        name: customerInfo.name || 'Bundle Customer',
        email: customerInfo.email,
        phone: customerInfo.phone,
        address: customerInfo.address,
        serviceInquired: bundleName,
        source: 'bundle-selection',
        status: 'new',
        smsConsent: customerInfo.smsConsent === true,
        additionalInfo: {
          bundleName,
          // Filter out undefined values from customerInfo
          ...Object.fromEntries(Object.entries(customerInfo).filter(([_, v]) => v !== undefined && v !== null && v !== ''))
        }
      };
      
      // Save client profile without blocking the main operation
      saveClientProfile(clientProfile).catch(profileError => {
        console.warn('⚠️ Client profile save failed (main bundle selection still saved):', profileError);
      });
    }

    return docRef.id;
  } catch (error) {
    console.error('Error saving bundle selection:', error);
    throw error;
  }
};

// Save service booking data (updated to also save client profile)
export const saveServiceBooking = async (serviceName: string, customerInfo: any) => {
  try {
    // Save to original collection for backward compatibility
    const docRef = await addDoc(collection(db, 'service-bookings'), {
      serviceName,
      customerInfo,
      timestamp: Timestamp.now(),
      status: 'new'
    });
    console.log('Service booking saved with ID: ', docRef.id);

    // Record SMS opt-in/opt-out when a phone number was provided (non-blocking)
    if (customerInfo.phone) {
      saveSmsConsent(
        customerInfo.phone,
        customerInfo.smsConsent === true,
        'service-booking',
        customerInfo.name,
        customerInfo.email
      );
    }

    // Also save to Client Profiles collection if customer info is provided
    if (customerInfo.email) {
      const clientProfile: ClientProfileData = {
        name: customerInfo.name || 'Service Customer',
        email: customerInfo.email,
        phone: customerInfo.phone,
        address: customerInfo.address,
        serviceInquired: serviceName,
        source: 'service-booking',
        status: 'new',
        smsConsent: customerInfo.smsConsent === true,
        additionalInfo: {
          serviceName,
          // Filter out undefined values from customerInfo
          ...Object.fromEntries(Object.entries(customerInfo).filter(([_, v]) => v !== undefined && v !== null && v !== ''))
        }
      };
      
      // Save client profile without blocking the main operation
      saveClientProfile(clientProfile).catch(profileError => {
        console.warn('⚠️ Client profile save failed (main service booking still saved):', profileError);
      });
    }

    return docRef.id;
  } catch (error) {
    console.error('Error saving service booking:', error);
    throw error;
  }
};

// Save commercial form data to Firestore (also save client profile)
export const saveCommercialForm = async (formData: CommercialFormData) => {
  try {
    // Save to commercial-inquiries collection
    const docRef = await addDoc(collection(db, 'commercial-inquiries'), {
      ...formData,
      smsConsent: formData.smsConsent === true,
      timestamp: Timestamp.now(),
      status: 'new',
      source: 'commercial-form'
    });
    console.log('Commercial inquiry saved with ID: ', docRef.id);

    // Record SMS opt-in/opt-out when a phone number was provided (non-blocking)
    if (formData.phoneNumber) {
      saveSmsConsent(
        formData.phoneNumber,
        formData.smsConsent === true,
        'commercial-form',
        formData.contactName
      );
    }

    // Also save to Client Profiles collection
    const clientProfile: ClientProfileData = {
      name: formData.contactName,
      email: '', // Commercial form doesn't collect email
      phone: formData.phoneNumber,
      address: formData.businessAddress,
      serviceInquired: 'Commercial Cleaning Services',
      source: 'commercial-form',
      status: 'new',
      smsConsent: formData.smsConsent === true,
      additionalInfo: {
        businessName: formData.businessName,
        isCommercial: true
      }
    };
    
    // Save client profile without blocking the main operation
    saveClientProfile(clientProfile).catch(profileError => {
      console.warn('⚠️ Client profile save failed (main commercial inquiry still saved):', profileError);
    });
    
    return docRef.id;
  } catch (error) {
    console.error('Error saving commercial inquiry:', error);
    throw error;
  }
}; 